import React, { useEffect, useState } from 'react'
import CustomRow from '../../../reusable-components/CustomRow'
import { Button, Card, Col, Space, Steps, message, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faChevronLeft, faChevronRight, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { handleError } from '../../../lib/utils'
import { useDispatch } from 'react-redux'
import CoreEnums from '../../../../core/utils/enums'
import coreReducer from '../../../../core/utils/reducer'
import { createCarePlanTemplate, updateCarePlanTemplate } from '../../utils/utils'
import GeneralForm from '../../Common/WizardSteps/GeneralForm'
import MealSummary from './MealSummary'

// Import meal-specific components
import DietaryPreferences from './DietaryPreferences'
import MealSchedule from './MealSchedule'
import NutritionalGoals from './NutritionalGoals'
import RecipesManagement from './RecipesManagement'
import ShoppingList from './ShoppingList'
import MealEducation from './MealEducation'

// Registry for tab keys by plan type
const getPlanTabKey = (planType, isMonitoring) => {
  const planTabKeys = {
    care: isMonitoring ? CoreEnums.tabKeys.MONITORING_PLANS : CoreEnums.tabKeys.CARE_PLANS,
    treatment: 'TREATMENT_PLANS',
    nutrition: 'NUTRITION_PLANS',
    wellness: 'WELLNESS_PLANS',
    preventive: 'PREVENTIVE_PLANS',
    rehabilitation: 'REHAB_PLANS',
    weight: 'WEIGHT_PLANS',
    meal: 'MEAL_PLANS'
  }

  return planTabKeys[planType] || CoreEnums.tabKeys.CARE_PLANS
}

const PlanTemplateForm = ({ isNew, recordData, dataPoints, refreshView, isMonitoring, planType, planLabel }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [general, setGeneral] = useState({
    name: recordData ? recordData.name : '',
    description: recordData ? recordData.description : ''
  })

  // Meal-specific state
  const [dietaryPreferences, setDietaryPreferences] = useState(recordData?.dietaryPreferences || [])
  const [mealSchedule, setMealSchedule] = useState(recordData?.mealSchedule || [])
  const [nutritionalGoals, setNutritionalGoals] = useState(recordData?.nutritionalGoals || [])
  const [recipes, setRecipes] = useState(recordData?.recipes || [])
  const [shoppingList, setShoppingList] = useState(recordData?.shoppingList || [])
  const [educationalResources, setEducationalResources] = useState(recordData?.educationalResources || [])

  const dispatch = useDispatch()
  const { token } = theme.useToken()

  // Display the correct plan type in UI
  const displayLabel = planLabel || 'Meal Plan'

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const checkGeneralStatus = () => {
    return general.name !== ''
  }

  const handleFinalizePlan = async () => {
    try {
      const payload = {
        name: general.name,
        description: general.description,
        planType: 'meal',
        dietaryPreferences,
        mealSchedule,
        nutritionalGoals,
        recipes,
        shoppingList,
        educationalResources
      }

      if (isNew) {
        await createCarePlanTemplate(payload)
        message.success('Meal plan created successfully!')
      } else {
        await updateCarePlanTemplate({
          ...payload,
          _id: recordData._id
        })
        message.success('Meal plan updated successfully!')
      }

      closeTab()
      refreshView()
    } catch (error) {
      message.error(handleError(error))
    }
  }

  const closeTab = () => {
    const tabKey = getPlanTabKey(planType, isMonitoring)
    dispatch(coreReducer.actions.closeTab(isNew ? `${tabKey}_new` : `${tabKey}_${recordData._id}`))
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Card
          size='small'
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <span>{isNew ? `Create New ${displayLabel}` : `Edit ${displayLabel}`}</span>
              <Button
                danger
                onClick={() => {
                  closeTab()
                }}
              >
                <FontAwesomeIcon icon={faXmarkCircle} />
              </Button>
            </Space>
          }
        >
          <Steps
            current={currentStep}
            onChange={(step) => {
              setCurrentStep(step)
            }}
            items={[
              {
                title: 'General Information',
                status: checkGeneralStatus() ? 'finish' : 'error'
              },
              {
                title: 'Dietary Preferences',
                status: dietaryPreferences.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Meal Schedule',
                status: mealSchedule.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Nutritional Goals',
                status: nutritionalGoals.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Recipes',
                status: recipes.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Shopping List',
                status: shoppingList.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Educational Resources',
                status: educationalResources.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Summary',
                status: 'wait'
              }
            ]}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card
          size='small'
          extra={
            <Space>
              {currentStep !== 0 ? (
                <Button
                  type='primary'
                  onClick={() => {
                    setCurrentStep(currentStep - 1)
                  }}
                  style={{ marginRight: 8 }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Previous
                </Button>
              ) : undefined}
              {currentStep !== 7 ? (
                <Button
                  disabled={currentStep === 0 && !checkGeneralStatus()}
                  type='primary'
                  onClick={() => {
                    setCurrentStep(currentStep + 1)
                  }}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              ) : (
                <Button
                  type='primary'
                  style={{ background: token.colorSuccess }}
                  onClick={() => {
                    handleFinalizePlan()
                  }}
                >
                  Finalize
                  <FontAwesomeIcon icon={faCheckCircle} />
                </Button>
              )}
            </Space>
          }
        >
          {currentStep === 0 && (
            <GeneralForm
              data={general}
              setData={setGeneral}
              planType={planType}
              planLabel={planLabel}
              isMonitoring={isMonitoring}
            />
          )}
          {currentStep === 1 && <DietaryPreferences data={dietaryPreferences} setData={setDietaryPreferences} />}
          {currentStep === 2 && <MealSchedule data={mealSchedule} setData={setMealSchedule} />}
          {currentStep === 3 && <NutritionalGoals data={nutritionalGoals} setData={setNutritionalGoals} />}
          {currentStep === 4 && (
            <RecipesManagement data={recipes} setData={setRecipes} dietaryPreferences={dietaryPreferences} />
          )}
          {currentStep === 5 && <ShoppingList data={shoppingList} setData={setShoppingList} recipes={recipes} />}
          {currentStep === 6 && <MealEducation data={educationalResources} setData={setEducationalResources} />}
          {currentStep === 7 && (
            <MealSummary
              general={general}
              dietaryPreferences={dietaryPreferences}
              mealSchedule={mealSchedule}
              nutritionalGoals={nutritionalGoals}
              recipes={recipes}
              shoppingList={shoppingList}
              educationalResources={educationalResources}
              isMonitoring={isMonitoring}
              setCurrentStep={setCurrentStep}
              planType={planType}
              planLabel={planLabel}
            />
          )}
        </Card>
      </Col>
    </CustomRow>
  )
}

export default PlanTemplateForm
